import { isEmpty } from 'lodash';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import ToastComponent from '../components/toast';
import { type ApiError, OpenAPI } from '../services/wealthlane-captable-services';
import { selectToken } from '../auth/authSlice';
import { selectTenantId } from '../pages/configuration/participantSignupSlice';
import { selectAuthRoutesList } from '../pages/configuration/routeSlice';
import { useNavigate } from 'react-router-dom';
import { attachDecimalConfigToRequest } from '../utils/apiDecimalConfig';
import { isSessionKilled } from '../services/apiInterceptor';

export function useWealthlaneCapTableApi(): {
  dismissError: () => void;
  error: ApiError | undefined;
  isLoading: boolean;
  handleRequest: <T>(request: Promise<T>, onSuccess?: () => void) => Promise<T | undefined>;
} {
  const navigate = useNavigate();
  const token = useSelector(selectToken);
  const tenantId = useSelector(selectTenantId);
  const userAuthList = useSelector(selectAuthRoutesList);
  const isPptView = userAuthList?.extraProperties?.[`ImpersonationInfo:ParticipantId`];
  const [error, setError] = useState<ApiError | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  OpenAPI.BASE = process.env.REACT_APP_WEALTHLANE_API_ENDPOINT ?? '';
  OpenAPI.TOKEN = token?.toString();
  OpenAPI.HEADERS = {
    [`X-Tenant-Id`]: tenantId === null ? '' : (tenantId as string),
  };

  const handleRequest = useCallback(async function <T>(
    request: Promise<T>,
    onSuccess?: () => void
  ) {
    if (isSessionKilled()) {
      return undefined;
    }
    setIsLoading(true);
    
    // Transfer decimal config metadata from promise to axios interceptor
    attachDecimalConfigToRequest(request);
    
    try {
      const response = await request;
      setError(undefined);
      /*  if (response != null && response !== undefined && response !== '') {
        toast.error(<ToastComponent label='Error' message={'Unable to fetch data'} />, {
          toastId: 2,
        });
      } */
      onSuccess && onSuccess();
      return response;
    } catch (exception: any) {
      if (isSessionKilled()) {
        return undefined;
      }
      setError(exception);
      if (exception?.body?.error?.validationErrors != null) {
        const error = exception?.body?.error?.validationErrors?.map((error: any) => {
          return error.message;
        });
        if (error[0] !== 'OTP is invalid') {
          toast.error(<ToastComponent label='Error' message={`${String(error)}`} />, {
            toastId: 1,
            autoClose: 30000,
          });
        }
        // toast.error(`${error}`, {
        //   toastId: 1,
        // });
      } else if (
        (typeof exception?.body?.error?.message === 'string' &&
          exception?.body?.error?.message.includes(
            'Index was outside the bounds of the array.'
          )) as boolean
      ) {
        toast.error(<ToastComponent label='Error' message={'Invalid data format'} />, {
          autoClose: 30000,
        });
      } else if (exception?.body?.error != null) {
        toast.error(<ToastComponent label='Error' message={exception?.body?.error?.message} />, {
          autoClose: 30000,
        });
      } else if (
        isPptView &&
        exception.status === 403 &&
        toast.error(
          <ToastComponent
            label='Error'
            message={'Action not allowed in read-only participant view.'}
          />,
          { autoClose: 30000 }
        )
      ) {
      } else if (
        exception.status === 401 ||
        (exception.status === 403 && isEmpty(exception?.body))
      ) {
        // handle conditional cases
      } else {
        // toast.error(
        //   <ToastComponent
        //     label='Error'
        //     message={'Server is down at the moment. Please try again after sometime.'}
        //   />,
        //   {
        //     toastId: 2,
        //   }
        // );
        navigate('/server-maintainance');
      }
    } finally {
      setIsLoading(false);
    }
  },
  []);

  function dismissError(): void {
    setError(undefined);
  }

  return { dismissError, error, isLoading, handleRequest };
}

export default useWealthlaneCapTableApi;
